/**
 * Límites de contenido para los mensajes de tickets (descripción y comentarios).
 *
 * Bajo la "opción D" las imágenes viajan embebidas como base64 dentro del HTML
 * del mensaje y se guardan así en BD. Para evitar que mensajes muy largos o con
 * muchas imágenes inflen la base de datos, validamos en el cliente —antes de
 * enviar— el peso total del HTML, la cantidad de imágenes y el largo del texto.
 *
 * Funciones puras (testeables); el componente las usa en `computed` para mostrar
 * un contador en vivo y deshabilitar el botón de envío al excederse.
 */

export interface MessageLimits {
  /** Peso máximo del HTML del mensaje en bytes (texto + imágenes base64). */
  maxBytes: number;
  /** Cantidad máxima de imágenes (`<img>`) por mensaje. */
  maxImages: number;
  /** Largo máximo del texto plano (sin etiquetas) en caracteres. */
  maxTextLength: number;
}

export const DEFAULT_MESSAGE_LIMITS: MessageLimits = {
  maxBytes: 1024 * 1024, // 1 MB
  maxImages: 5,
  maxTextLength: 5000,
};

export interface MessageContentStats {
  bytes: number;
  imageCount: number;
  textLength: number;
}

/** Tamaño en bytes (UTF-8) del HTML. */
export function htmlByteSize(html: string): number {
  return new TextEncoder().encode(html).length;
}

/** Cantidad de etiquetas `<img>` en el HTML. */
export function countHtmlImages(html: string): number {
  const matches = html.match(/<img\b/gi);
  return matches ? matches.length : 0;
}

/** Largo del texto plano (sin etiquetas ni `&nbsp;`), ya recortado. */
export function htmlPlainTextLength(html: string): number {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length;
}

/** Analiza el HTML y devuelve sus métricas de tamaño/contenido. */
export function analyzeMessageContent(html: string): MessageContentStats {
  return {
    bytes: htmlByteSize(html),
    imageCount: countHtmlImages(html),
    textLength: htmlPlainTextLength(html),
  };
}

/** Formatea un tamaño en bytes a un string legible (B / KB / MB). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Devuelve los mensajes de error de los límites excedidos. Array vacío si el
 * contenido está dentro de todos los límites.
 */
export function getMessageLimitErrors(
  stats: MessageContentStats,
  limits: MessageLimits = DEFAULT_MESSAGE_LIMITS
): string[] {
  const errors: string[] = [];
  if (stats.bytes > limits.maxBytes) {
    errors.push(`El mensaje supera el peso máximo (${formatBytes(limits.maxBytes)}).`);
  }
  if (stats.imageCount > limits.maxImages) {
    errors.push(`Máximo ${limits.maxImages} imágenes por mensaje.`);
  }
  if (stats.textLength > limits.maxTextLength) {
    errors.push(`Máximo ${limits.maxTextLength} caracteres de texto.`);
  }
  return errors;
}

/** `true` si el contenido respeta todos los límites. */
export function isWithinMessageLimits(
  stats: MessageContentStats,
  limits: MessageLimits = DEFAULT_MESSAGE_LIMITS
): boolean {
  return getMessageLimitErrors(stats, limits).length === 0;
}
